import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Star, PenLine, Check, ImagePlus, X } from "lucide-react";
import { useCruise } from "../context/CruiseContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compress an image file client-side to a small JPEG data URL (max 500px, ~0.7 quality)
const compressImage = (file, maxSize = 500) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ReviewForm = () => {
  const { EVENT } = useCruise();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trip: "", quote: "" });
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState("");

  const reset = () => {
    setForm({ name: "", trip: "", quote: "" });
    setRating(5);
    setPhoto("");
    setDone(false);
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG or WEBP).");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("That image is too large. Please pick one under 15MB.");
      return;
    }
    try {
      const data = await compressImage(file);
      setPhoto(data);
    } catch {
      toast.error("Couldn't process that image. Try another.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote) {
      toast.error("Please add your name and your review.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, rating, photo, cruise: EVENT.id });
      setDone(true);
      toast.success("Thank you! Your review will be featured after a quick review.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTimeout(reset, 200); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full px-7 py-5 h-auto">
          <PenLine size={16} className="mr-2" /> Share Your Story
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111111] border-white/15 text-white rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-cond uppercase tracking-wide text-white text-2xl">
            Share Your <span className="text-[#E50914]">Story</span>
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center text-center py-8">
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1B8A5A] text-white mb-4">
              <Check size={26} />
            </span>
            <p className="font-cond uppercase tracking-wide text-white text-lg">Thank you!</p>
            <p className="text-white/55 text-sm mt-1 max-w-xs">
              Your review has been submitted and will be featured after a quick approval.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Your Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Jamie L."
                  className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                />
              </div>
              <div>
                <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Which Sailing?</Label>
                <Input
                  value={form.trip}
                  onChange={(e) => setForm({ ...form, trip: e.target.value })}
                  placeholder="e.g. HHAS 2025"
                  className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Your Rating</Label>
              <div className="flex gap-1.5 mt-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHover(s)}
                    onMouseLeave={() => setHover(0)}
                    aria-label={`${s} stars`}
                  >
                    <Star
                      size={26}
                      className={`transition-colors ${(hover || rating) >= s ? "text-[var(--hh-gold)] fill-[var(--hh-gold)]" : "text-white/25"}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Add A Photo (optional)</Label>
              {photo ? (
                <div className="mt-1.5 flex items-center gap-3">
                  <img src={photo} alt="preview" className="w-16 h-16 object-cover rounded-full border-2 border-[var(--hh-gold)]" />
                  <button
                    type="button"
                    onClick={() => setPhoto("")}
                    className="inline-flex items-center gap-1 text-white/60 hover:text-[#E50914] text-xs font-cond uppercase tracking-widest"
                  >
                    <X size={14} /> Remove
                  </button>
                </div>
              ) : (
                <label className="mt-1.5 flex items-center gap-2 cursor-pointer bg-[#0A0A0A] border border-white/15 border-dashed px-4 py-3 text-white/60 hover:text-white hover:border-[#E50914] transition-colors">
                  <ImagePlus size={18} className="text-[var(--hh-gold)]" />
                  <span className="text-sm">Choose a photo from your trip</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="hidden" />
                </label>
              )}
              <p className="text-white/35 text-[11px] mt-1.5">JPG, PNG or WEBP — auto-resized, kept under 2MB. Featured after approval.</p>
            </div>

            <div>
              <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Your Review</Label>
              <Textarea
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                placeholder="Tell us about your experience at sea..."
                className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914] min-h-[110px]"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full py-6 h-auto"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </Button>
            <p className="text-white/40 text-xs text-center">Reviews are featured after a quick approval.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewForm;
